/*
 * Copyright 2026 Sophos Limited. All rights reserved.
 *
 * 'Sophos' and 'Sophos Anti-Virus' are registered trademarks of Sophos Limited and Sophos Group. All other product
 * and company names mentioned are trademarks or registered trademarks of their respective owners.
 */
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useState, type ReactElement } from "react";
import { type DLQItem, type DLQMessageSummary, type DLQResult } from "../../services/dailyChecksApi";

type DLQTableProps = {
  /** Current DLQ items */
  data: DLQResult;
};

/** Formats an ISO timestamp into a readable locale string, or returns fallback text if invalid */
function formatTimestamp(ts?: string): string {
  if (!ts) return "No timestamp";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Parses and pretty-prints a raw message body, handling nested JSON if present */
function formatRawBody(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    if (parsed.Message && typeof parsed.Message === "string") {
      try {
        parsed.Message = JSON.parse(parsed.Message);
      } catch {
        // keep as-is
      }
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

/** Renders a single DLQ message row with expandable raw body preview */
function DLQMessageRow({ msg }: { msg: DLQMessageSummary }): ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: "pointer", "& td": { borderBottom: 0, py: 0.5 } }}>
        <TableCell sx={{ width: 28, pr: 0 }}>
          <IconButton size="small" aria-label={open ? "collapse message" : "expand message"}>
            {open ? <KeyboardArrowDownIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(msg.timestamp)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: 320,
                overflowY: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1,
                mb: 1,
              }}
            >
              {formatRawBody(msg.rawBody)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** Renders a DLQ queue row with expandable message details */
function DLQRow({ item }: { item: DLQItem }): ReactElement {
  const [open, setOpen] = useState(false);
  const hasMessages = item.messages.length > 0;

  return (
    <>
      <TableRow
        hover={hasMessages}
        onClick={() => hasMessages && setOpen(!open)}
        sx={{
          cursor: hasMessages ? "pointer" : "default",
          "& td, & th": { borderBottom: open ? 0 : undefined },
        }}
      >
        <TableCell sx={{ width: 28, pr: 0 }}>
          {hasMessages && (
            <IconButton size="small" aria-label={open ? "collapse queue" : "expand queue"}>
              {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{item.queuePhysicalName ?? item.queueName}</TableCell>
        <TableCell>{item.region}</TableCell>
        <TableCell align="right">{item.count}</TableCell>
      </TableRow>
      {hasMessages && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ mx: 2, my: 1 }}>
                <Table size="small">
                  <TableBody>
                    {item.messages.map((msg, i) => (
                      <DLQMessageRow key={i} msg={msg} />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** Renders DLQ counts per queue and region with expandable message previews */
export function DLQTable({ data }: DLQTableProps): ReactElement {
  return (
    <TableContainer>
      <Table size="small" aria-label="dlq-summary">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 28 }} />
            <TableCell>Queue</TableCell>
            <TableCell>Region</TableCell>
            <TableCell align="right">Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.items.map((item, i) => (
            <DLQRow key={i} item={item} />
          ))}
          <TableRow>
            <TableCell />
            <TableCell colSpan={2}>
              <strong>Total</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{data.totalCount}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
